export const M3_REVIEW = {
  id: "m3-review",
  title: "Module 3 Final Review",
  topic: "Test everything you learned in Module 3! From abilities to social plans, this review challenges you with integrated exercises from A1 to B1 level. Show what you know!",
  iconType: "trophy",
  grammarImage: "/m3-review-final.jpg",
  grammarText: "Final comprehensive review of Module 3. Integrates all concepts: abilities, can/can't, adverbs, modifiers, connectors, desires, opinions, and social plans. Five challenging sets to consolidate your B1 level!",
  sets: [
    // ===================================================================
    // SET 1: ABILITIES + CAN/CAN'T (Units 11-12) - 15 preguntas
    // ===================================================================
    {
      id: "m3-final-review-s1",
      title: "Review Set 1: Abilities & Can",
      questions: [
        {
          type: "mc",
          q: "Complete: 'I'm _____ cooking, so I _____ cook very well.'",
          options: ["good at / can", "good in / can", "good at / cans"],
          a: "good at / can",
          tip: "'Good AT' + activity! Can = ability shown!",
          successtip: "✓ GOOD AT / CAN! Perfect combo! 👨‍🍳 Ability + capacity! B1 integration!"
        },
        {
          type: "mc",
          q: "Which is CORRECT? 'She's bad _____ math.'",
          options: ["at", "in", "on"],
          a: "at",
          tip: "'Bad AT' = preposition for abilities!",
          successtip: "✓ BAD AT! Correct preposition! 📐 Fixed phrase! Grammar mastered!"
        },
        {
          type: "mc",
          q: "Integration: 'I can swim, but I _____ swim very fast.'",
          options: ["can't", "doesn't", "don't"],
          a: "can't",
          tip: "'Can' negative = 'can't'! Modal verb!",
          successtip: "✓ CAN'T! Modal negative! 🏊 Can BUT can't fast! Honest limitation!"
        },
        {
          type: "mc",
          q: "Complete: 'He's good at soccer because he _____ play very well.'",
          options: ["can", "cans", "is can"],
          a: "can",
          tip: "'Can' never changes! No 's' for third person!",
          successtip: "✓ CAN! No 's' on modals! ⚽ Good at = can! Logic clear!"
        },
        {
          type: "mc",
          q: "ERROR CHECK: Which is WRONG? '_____'",
          options: ["I'm good in cooking", "I'm good at cooking", "I can cook"],
          a: "I'm good in cooking",
          tip: "Never 'good IN'! Always 'good AT'!",
          successtip: "✓ GOOD IN = WRONG! 🚫 Good AT only! Preposition rule!"
        },
        {
          type: "match",
          q: "Match abilities to correct structures:",
          pairs: [
            { left: "I'm good at", right: "cooking (gerund)" },
            { left: "I can", right: "cook (base verb)" },
            { left: "I'm bad at", right: "swimming (gerund)" }
          ],
          a: "completed",
          tip: "Good/bad AT + -ing! Can + base verb!",
          successtip: "✓ STRUCTURES MATCHED! 🎯 Different patterns! Grammar clear!"
        },
        {
          type: "mc",
          q: "Complete: 'We _____ good at English, but we _____ speak fluently yet.'",
          options: ["are / can't", "is / can't", "are / doesn't"],
          a: "are / can't",
          tip: "'We' = are! Can't = modal negative!",
          successtip: "✓ ARE / CAN'T! Two verbs! 🗣️ Good at BUT not fluent yet! Realistic!"
        },
        {
          type: "mc",
          q: "Integration: 'She can drive, and she's _____ it.'",
          options: ["good at", "good in", "good for"],
          a: "good at",
          tip: "'Good at' + -ing (driving = it)!",
          successtip: "✓ GOOD AT! Can + good at! 🚗 Double confirmation! Skilled driver!"
        },
        {
          type: "mc",
          q: "Which shows ABILITY? '_____'",
          options: ["I can cook very well", "I like cooking", "I want to cook"],
          a: "I can cook very well",
          tip: "'Can' = ability! Like = opinion! Want = desire!",
          successtip: "✓ CAN! Ability statement! 👨‍🍳 Not opinion or desire! Clear distinction!"
        },
        {
          type: "mc",
          q: "Complete: 'They're bad at _____, so they _____ swim.'",
          options: ["swimming / can't", "swim / can't", "swimming / doesn't"],
          a: "swimming / can't",
          tip: "Bad at + -ing! Can't = ability lack!",
          successtip: "✓ SWIMMING / CAN'T! Gerund + modal! 🏊 Bad at = can't! Logical!"
        },
        {
          type: "mc",
          q: "ERROR CHECK: 'I'm good at speak English.' — What's WRONG?",
          options: ["Should be 'speaking' (gerund)", "Should be 'the English'", "Nothing wrong"],
          a: "Should be 'speaking' (gerund)",
          tip: "Good/bad AT + -ING! Always gerund!",
          successtip: "✓ SPEAKING! Gerund needed! 🗣️ At + -ing! Common error fixed!"
        },
        {
          type: "mc",
          q: "Complete: 'My son _____ good at math, so he _____ solve problems easily.'",
          options: ["is / can", "are / can", "is / cans"],
          a: "is / can",
          tip: "'Son' = he = is! Can = no 's'!",
          successtip: "✓ IS / CAN! Third person! 📐 Good at = can! Cause-effect!"
        },
        {
          type: "mc",
          q: "Which is MORE NATURAL? 'I'm good at _____.'",
          options: ["cooking", "to cook", "cook"],
          a: "cooking",
          tip: "Good at + GERUND! Most natural!",
          successtip: "✓ COOKING! Gerund! 👨‍🍳 Good at + -ing! Natural English!"
        },
        {
          type: "input",
          q: "Fill in: 'She _____ (be) bad at dancing, but she _____ (can) learn!'",
          a: "is / can",
          accept: { ignoreCase: true, variants: ["is, can", "is/can", "'s / can"] },
          tip: "Be form + can! Negative ability + possibility!",
          successtip: "✓ IS / CAN! Bad at BUT can learn! 💃 Hope + effort! Growth mindset!"
        },
        {
          type: "mc",
          q: "INTEGRATION: 'I'm good at cooking, I can cook well, and I _____ cooking!' (Add opinion)",
          options: ["love", "am love", "can love"],
          a: "love",
          tip: "Opinion verb! I love = simple present!",
          successtip: "✓ LOVE! Three concepts! 👨‍🍳 Ability + can + opinion! Set 1 complete! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 2: ADVERBS + MODIFIERS (Units 13-14) - 15 preguntas
    // ===================================================================
    {
      id: "m3-final-review-s2",
      title: "Review Set 2: Adverbs & Modifiers",
      questions: [
        {
          type: "mc",
          q: "Complete: 'She speaks English _____ .' (How?)",
          options: ["well", "good", "nice"],
          a: "well",
          tip: "'Well' = adverb! Describes HOW she speaks!",
          successtip: "✓ WELL! Adverb! 🗣️ Speaks HOW = well! Verb modifier!"
        },
        {
          type: "mc",
          q: "Add modifier: 'I can speak English _____ well.' (intensify)",
          options: ["very", "much", "many"],
          a: "very",
          tip: "'Very' + adverb! Intensifies 'well'!",
          successtip: "✓ VERY! Modifier! 🔊 Very well = intensified! Better English!"
        },
        {
          type: "mc",
          q: "Which is CORRECT? 'He works _____ .'",
          options: ["carefully", "careful", "care"],
          a: "carefully",
          tip: "Adverb form! Careful + ly = carefully!",
          successtip: "✓ CAREFULLY! Adverb -ly! 💼 Works HOW = carefully! Quality work!"
        },
        {
          type: "mc",
          q: "Integration: 'I walk _____ when I'm tired.' (manner)",
          options: ["slowly", "slow", "slower"],
          a: "slowly",
          tip: "'Slowly' = adverb! Describes walking!",
          successtip: "✓ SLOWLY! Walk HOW! 🚶 Tired = slow walk! Logical!"
        },
        {
          type: "match",
          q: "Match modifiers to their intensity:",
          pairs: [
            { left: "very", right: "high intensity (neutral)" },
            { left: "really", right: "very high (emphatic)" },
            { left: "quite", right: "moderate-high" }
          ],
          a: "completed",
          tip: "Different levels of intensity!",
          successtip: "✓ MODIFIERS MATCHED! 🔊 Very vs really vs quite! Intensity clear!"
        },
        {
          type: "mc",
          q: "Complete: 'She sings _____ well!' (strong emphasis)",
          options: ["really", "real", "much"],
          a: "really",
          tip: "'Really' = emphatic modifier! Strong!",
          successtip: "✓ REALLY! Emphatic! 🎤 Really well = very impressive! Strong praise!"
        },
        {
          type: "mc",
          q: "ERROR CHECK: 'I speak English very good.' — What's WRONG?",
          options: ["Should be 'well' (adverb)", "Should be 'the English'", "Nothing wrong"],
          a: "Should be 'well' (adverb)",
          tip: "'Good' = adjective! Need 'well' (adverb)!",
          successtip: "✓ WELL! Adverb needed! 🗣️ Speak well, not good! Common error!"
        },
        {
          type: "mc",
          q: "Complete: 'I can swim _____ well now.' (moderate)",
          options: ["quite", "very", "really"],
          a: "quite",
          tip: "'Quite' = moderate intensity! Not too strong!",
          successtip: "✓ QUITE! Moderate! 🏊 Quite well = pretty good! Honest assessment!"
        },
        {
          type: "mc",
          q: "Which describes ACTION? '_____'",
          options: ["He works quickly", "He is quick", "He has quick"],
          a: "He works quickly",
          tip: "Adverb describes verb! Works HOW!",
          successtip: "✓ WORKS QUICKLY! Action description! ⚡ Verb + adverb! Speed work!"
        },
        {
          type: "mc",
          q: "Complete: 'They play soccer _____ .' (skillfully)",
          options: ["well", "good", "fine"],
          a: "well",
          tip: "'Well' = adverb! Play HOW = well!",
          successtip: "✓ WELL! Play well! ⚽ Adverb for skill! Good players!"
        },
        {
          type: "mc",
          q: "Integration: 'I'm good at cooking, so I can cook _____ well.' (add modifier)",
          options: ["quite", "much", "so"],
          a: "quite",
          tip: "'Quite well' = moderate skill level!",
          successtip: "✓ QUITE! Integration! 👨‍🍳 Good at = can cook quite well! Logical!"
        },
        {
          type: "mc",
          q: "Complete: 'She speaks _____ quietly because the baby is sleeping.' (reason)",
          options: ["very", "much", "so"],
          a: "very",
          tip: "'Very quietly' = high intensity quietness!",
          successtip: "✓ VERY! Baby sleeping! 👶 Very quietly = considerate! Context clear!"
        },
        {
          type: "mc",
          q: "Which is MOST EMPHATIC? 'I'm _____ tired!'",
          options: ["really", "quite", "very"],
          a: "really",
          tip: "'Really' = most emphatic! Strongest feeling!",
          successtip: "✓ REALLY! Maximum emphasis! 😴 Really tired = exhausted! Strong feeling!"
        },
        {
          type: "input",
          q: "Complete: 'He drives _____ (careful + ly) and _____ (slow + ly).'",
          a: "carefully / slowly",
          accept: { ignoreCase: true, variants: ["carefully, slowly", "carefully/slowly"] },
          tip: "Two adverbs! Add -ly to both adjectives!",
          successtip: "✓ CAREFULLY / SLOWLY! Two adverbs! 🚗 Safe driver! Manner clear!"
        },
        {
          type: "mc",
          q: "FINAL: 'I can speak English quite well because I practice very carefully every day!' — How many modifiers?",
          options: ["Two (quite, very)", "One (quite)", "Three (quite, very, carefully)"],
          a: "Two (quite, very)",
          tip: "'Carefully' = adverb, not modifier! Quite + very = modifiers!",
          successtip: "✓ TWO! Quite + very! 📚 Adverbs ≠ modifiers! Set 2 complete! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 3: CONNECTORS + LOGIC (Unit 14) - 15 preguntas
    // ===================================================================
    {
      id: "m3-final-review-s3",
      title: "Review Set 3: Connectors & Logic",
      questions: [
        {
          type: "mc",
          q: "REASON: 'I want to learn English _____ I need it for work.'",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason! WHY you want it!",
          successtip: "✓ BECAUSE! Reason! 📚 Want + BECAUSE = explain why! Logic!"
        },
        {
          type: "mc",
          q: "RESULT: 'I practice every day, _____ I can speak quite well.'",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = result! Practice → speak well!",
          successtip: "✓ SO! Result! 💪 Action → consequence! Cause-effect!"
        },
        {
          type: "mc",
          q: "CONTRAST: 'I like my job, _____ the salary is low.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = contrast! Like BUT problem!",
          successtip: "✓ BUT! Contrast! 💼 Like BUT low pay! Reality conflict!"
        },
        {
          type: "match",
          q: "Match connectors to their functions:",
          pairs: [
            { left: "because", right: "Shows reason/cause" },
            { left: "so", right: "Shows result/effect" },
            { left: "but", right: "Shows contrast/opposition" }
          ],
          a: "completed",
          tip: "Three different logical functions!",
          successtip: "✓ CONNECTORS MATCHED! 🔗 Because/So/But! Logic mastered!"
        },
        {
          type: "mc",
          q: "Complete: 'She's tired _____ she worked 12 hours.'",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains WHY tired! Reason!",
          successtip: "✓ BECAUSE! 12 hours = reason! 😴 Long work = tired! Clear cause!"
        },
        {
          type: "mc",
          q: "Complete: 'I want to save money, _____ I work two jobs.'",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' shows action taken! Want → so work!",
          successtip: "✓ SO! Action! 💰 Goal = save, SO work two jobs! Hustle!"
        },
        {
          type: "mc",
          q: "Complete: 'I want to rest, _____ I have to work.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = desire vs obligation! Want ≠ have to!",
          successtip: "✓ BUT! Conflict! 😔 Want rest BUT must work! Worker reality!"
        },
        {
          type: "mc",
          q: "ERROR CHECK: Which connector is WRONG? 'I like soccer _____ it's fun.'",
          options: ["so (wrong - should be because)", "because (correct)", "but (wrong)"],
          a: "so (wrong - should be because)",
          tip: "Fun = reason to like! Need 'because'!",
          successtip: "✓ SO WRONG! 🚫 Fun = reason! Need BECAUSE! Logic clear!"
        },
        {
          type: "mc",
          q: "Integration: 'I'm good at cooking _____ I practice, _____ I want to be a chef.'",
          options: ["because / so", "so / because", "but / so"],
          a: "because / so",
          tip: "Reason first (because), then result (so)!",
          successtip: "✓ BECAUSE / SO! Two connectors! 👨‍🍳 Practice = reason, chef = goal! Perfect!"
        },
        {
          type: "mc",
          q: "Complete: 'He doesn't like his job _____ the boss is mean.'",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains WHY he doesn't like!",
          successtip: "✓ BECAUSE! Mean boss = reason! 😤 Negative cause! Logic clear!"
        },
        {
          type: "mc",
          q: "Complete: 'She studies hard, _____ she gets good grades.'",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = result! Study → good grades!",
          successtip: "✓ SO! Study = cause, grades = effect! 📚 Effort pays! Success!"
        },
        {
          type: "mc",
          q: "Complete: 'I want to buy a house, _____ I don't have enough money.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = dream vs reality! Want BUT can't!",
          successtip: "✓ BUT! Dream vs reality! 🏠💰 Want BUT barrier! Common struggle!"
        },
        {
          type: "mc",
          q: "Which shows CAUSE → EFFECT? '_____'",
          options: ["I'm tired, so I'll rest", "I'm tired because I worked", "I'm tired, but I'll work"],
          a: "I'm tired, so I'll rest",
          tip: "'So' = effect/action! Tired → rest!",
          successtip: "✓ SO! Cause-effect! 😴 State → action! Logical response!"
        },
        {
          type: "input",
          q: "Complete logic: 'I want to learn English _____ I need it, _____ I study every day.'",
          a: "because / so",
          accept: { ignoreCase: true, variants: ["because, so", "because/so"] },
          tip: "Reason (because) + Action (so)!",
          successtip: "✓ BECAUSE / SO! Complete logic! 📚 Need → study! Perfect chain!"
        },
        {
          type: "mc",
          q: "COMPLEX: 'I like my job because the people are nice, but the salary is low, so I'm looking for a new one.' — How many connectors?",
          options: ["Three (because, but, so)", "Two (because, but)", "Four"],
          a: "Three (because, but, so)",
          tip: "Count all: because + but + so = 3!",
          successtip: "✓ THREE! Because + but + so! 🔗 Complex B1 sentence! Set 3 done! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 4: DESIRES + INVITATIONS (Units 15-16) - 15 preguntas
    // ===================================================================
    {
      id: "m3-final-review-s4",
      title: "Review Set 4: Wants & Social Plans",
      questions: [
        {
          type: "mc",
          q: "CASUAL: Talking to friend: 'I _____ to go home.'",
          options: ["want", "would like", "'d like"],
          a: "want",
          tip: "Friend = casual! 'Want' is direct!",
          successtip: "✓ WANT! Casual! 😊 Friends = direct language! No formality!"
        },
        {
          type: "mc",
          q: "FORMAL: Job interview: 'I _____ to work for your company.'",
          options: ["would like", "want", "wanna"],
          a: "would like",
          tip: "Interview = formal! 'Would like' professional!",
          successtip: "✓ WOULD LIKE! Formal! 💼 Interview = polite! Professional language!"
        },
        {
          type: "mc",
          q: "Complete: 'She _____ to be a doctor when she grows up.'",
          options: ["wants", "want", "wanting"],
          a: "wants",
          tip: "Third person = wants (with 's')!",
          successtip: "✓ WANTS! Third person! 👩‍⚕️ She wants! Career dream!"
        },
        {
          type: "mc",
          q: "NEGATIVE: 'I _____ want to work on weekends.'",
          options: ["don't", "doesn't", "wouldn't"],
          a: "don't",
          tip: "'I don't want' = present simple negative!",
          successtip: "✓ DON'T! Present negative! 📅 Weekends = family time! Valid!"
        },
        {
          type: "match",
          q: "Match contexts to correct forms:",
          pairs: [
            { left: "Job interview", right: "I would like to..." },
            { left: "With friends", right: "I want to..." },
            { left: "Very casual", right: "Wanna...? (recognize)" }
          ],
          a: "completed",
          tip: "Context determines formality!",
          successtip: "✓ CONTEXTS MATCHED! 🎯 Formal vs casual! Social awareness!"
        },
        {
          type: "mc",
          q: "INVITATION POLITE: '_____ you like to go to the movies?'",
          options: ["Would", "Do", "Are"],
          a: "Would",
          tip: "'Would you like' = polite invitation!",
          successtip: "✓ WOULD! Polite invite! 🎬 Would = respectful! Good manners!"
        },
        {
          type: "mc",
          q: "INVITATION CASUAL: '_____ you want to play soccer?'",
          options: ["Do", "Would", "Are"],
          a: "Do",
          tip: "'Do you want' = casual invitation!",
          successtip: "✓ DO! Casual! ⚽ Friends = informal! Natural speech!"
        },
        {
          type: "mc",
          q: "ACCEPT ENTHUSIASTIC: 'Would you like to come?' — '_____!'",
          options: ["Yes, I'd love to", "Maybe", "I don't know"],
          a: "Yes, I'd love to",
          tip: "'I'd love to' = enthusiastic yes!",
          successtip: "✓ I'D LOVE TO! Enthusiastic! 🎉 Strong positive! Good response!"
        },
        {
          type: "mc",
          q: "DECLINE POLITE: 'I'm sorry, I can't. I _____ work.'",
          options: ["have to", "want to", "like to"],
          a: "have to",
          tip: "'Have to' = obligation! Polite excuse!",
          successtip: "✓ HAVE TO! Obligation! 😔 Polite decline + reason! Respectful!"
        },
        {
          type: "mc",
          q: "Complete: 'I _____ like math.' (school subject - zero article)",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "'I don't like' = opinion! No article before subject!",
          successtip: "✓ DON'T LIKE! Opinion + zero article! 📐 Math = no 'the'!"
        },
        {
          type: "mc",
          q: "Complete: 'She _____ cooking because it's relaxing.'",
          options: ["loves", "love", "loving"],
          a: "loves",
          tip: "Third person = loves! Strong positive!",
          successtip: "✓ LOVES! Third person! 👩‍🍳 Strong positive + reason! Passion!"
        },
        {
          type: "mc",
          q: "Integration: 'I want to learn English, so I study every day because I _____ it!' (add opinion)",
          options: ["like", "can", "am"],
          a: "like",
          tip: "Opinion! 'I like it' = enjoy!",
          successtip: "✓ LIKE! Opinion added! 📚 Want + study + like! Full motivation!"
        },
        {
          type: "mc",
          q: "SLANG RECOGNITION: 'Wanna grab coffee?' means '_____'",
          options: ["Want to get coffee?", "Would you like coffee?", "Do you have coffee?"],
          a: "Want to get coffee?",
          tip: "'Wanna' = want to! 'Grab' = get casually!",
          successtip: "✓ WANT TO GET! Slang! ☕ Wanna = want to! Street language!"
        },
        {
          type: "input",
          q: "Complete invitation: '_____ you _____ to go to the movies?' (polite)",
          a: "Would / like",
          accept: { ignoreCase: true, variants: ["would, like", "would/like"] },
          tip: "'Would you like' = polite invitation structure!",
          successtip: "✓ WOULD / LIKE! Polite invitation! 🎬 Formal offer! Social skill!"
        },
        {
          type: "mc",
          q: "FINAL INTEGRATION: 'I want to be a chef because I love cooking, so I'm taking classes. Would you like to come to my restaurant when I open it?' — How many concepts?",
          options: ["Five (want, love, so, because, invitation)", "Three", "Two"],
          a: "Five (want, love, so, because, invitation)",
          tip: "Count: want + love + because + so + invitation!",
          successtip: "✓ FIVE CONCEPTS! 👨‍🍳 Complete integration! Set 4 complete! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 5: COMPLETE INTEGRATION (All Units 11-16) - 15 preguntas
    // ===================================================================
    {
      id: "m3-final-review-s5",
      title: "Review Set 5: Complete Integration",
      questions: [
        {
          type: "mc",
          q: "COMPLETE: 'I'm _____ at cooking, I can cook _____, and I _____ it!'",
          options: ["good / well / love", "good / good / like", "well / well / love"],
          a: "good / well / love",
          tip: "Good at + adverb well + opinion love!",
          successtip: "✓ GOOD/WELL/LOVE! Three units! 👨‍🍳 Ability + manner + opinion! Perfect!"
        },
        {
          type: "mc",
          q: "ADD MODIFIER: 'I can speak English _____ well because I practice every day.'",
          options: ["quite", "much", "many"],
          a: "quite",
          tip: "'Quite well' = moderate skill! Modifier!",
          successtip: "✓ QUITE! Modifier! 🗣️ Quite well = honest! Practice = reason!"
        },
        {
          type: "mc",
          q: "ADD CONNECTOR: 'I want to learn English _____ I need it for work.'",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' explains reason! Need = why!",
          successtip: "✓ BECAUSE! Reason! 💼 Clear motivation! Logic perfect!"
        },
        {
          type: "mc",
          q: "COMPLEX: 'I'm good at soccer, so I play very well, but I want to play even better!' — Count grammar concepts:",
          options: ["Five (good at, so, adverb, but, want)", "Three", "Four"],
          a: "Five (good at, so, adverb, but, want)",
          tip: "Good at + so + very well + but + want = 5!",
          successtip: "✓ FIVE! Complete! ⚽ All Module 3! B1 sentence!"
        },
        {
          type: "mc",
          q: "ERROR CHECK: 'I'm good in cooking and I can cook very good.' — How many errors?",
          options: ["Two (good IN / very good)", "One (good in)", "Zero"],
          a: "Two (good IN / very good)",
          tip: "Good AT (not in)! Adverb WELL (not good)!",
          successtip: "✓ TWO ERRORS! 🚫 Good AT + very WELL! Common mistakes caught!"
        },
        {
          type: "match",
          q: "FINAL MATCH: Complete the progression:",
          pairs: [
            { left: "Unit 11-12", right: "I'm good at X / I can X" },
            { left: "Unit 13-14", right: "I do X well / quite well" },
            { left: "Unit 15-16", right: "I want to X / I like X" }
          ],
          a: "completed",
          tip: "Three pairs of units! Progression!",
          successtip: "✓ PROGRESSION! 🎯 Module 3 journey! A2 → B1!"
        },
        {
          type: "mc",
          q: "INTEGRATION: 'She's bad at math, _____ she studies very hard, _____ she can pass the test.'",
          options: ["but / so", "so / but", "because / but"],
          a: "but / so",
          tip: "Contrast (but) + result (so)! Two connectors!",
          successtip: "✓ BUT / SO! Double connector! 📚 Weakness BUT effort SO success!"
        },
        {
          type: "mc",
          q: "FULL SENTENCE: 'I _____ to be a doctor _____ I _____ helping people.'",
          options: ["want / because / love", "wants / so / like", "want / so / can"],
          a: "want / because / love",
          tip: "Want + because + love! Desire + reason + opinion!",
          successtip: "✓ WANT/BECAUSE/LOVE! Three concepts! 👩‍⚕️ Career motivation complete!"
        },
        {
          type: "mc",
          q: "INVITATION COMPLETE: 'I'm _____ at cooking, so _____ you _____ to come to dinner?'",
          options: ["good / would / like", "good / do / want", "well / would / like"],
          a: "good / would / like",
          tip: "Good at + would you like (polite invitation)!",
          successtip: "✓ GOOD/WOULD/LIKE! Full invite! 🍽️ Ability → invitation! Social!"
        },
        {
          type: "mc",
          q: "COMPLEX B1: 'I'm really good at English because I study quite hard every day, so I can speak very well now, but I want to be fluent!' — This sentence uses:",
          options: ["All Module 3 concepts!", "Only Unit 11", "Only Unit 14"],
          a: "All Module 3 concepts!",
          tip: "Really + good at + because + quite + every day + so + can + very well + but + want!",
          successtip: "✓ ALL CONCEPTS! 🔥 Abilities + adverbs + modifiers + connectors + can + want! B1 mastery!"
        },
        {
          type: "mc",
          q: "SOCIAL COMPLETE: 'I _____ playing soccer, and I'm _____ at it. _____ you _____ to play with me?'",
          options: ["love / good / Would / like", "like / well / Do / want", "love / well / Would / want"],
          a: "love / good / Would / like",
          tip: "Love + good at + would you like (polite)!",
          successtip: "✓ LOVE/GOOD/WOULD/LIKE! Social integration! ⚽ Opinion + ability + invite!"
        },
        {
          type: "mc",
          q: "ERROR HUNT: 'I'm good at the soccer and I can play very good but I want play professional.' — How many errors?",
          options: ["Four (the soccer, very good, want play, no 'to')", "Two", "Three"],
          a: "Four (the soccer, very good, want play, no 'to')",
          tip: "Zero article + very WELL + want TO play + professionally!",
          successtip: "✓ FOUR! 🚫 Many errors caught! Soccer (no 'the') + well + want TO + -ly! Review skills sharp!"
        },
        {
          type: "input",
          q: "ULTIMATE: 'I'm good _____ cooking, I can cook _____ , I _____ it, and I _____ to be a chef!'",
          a: "at / well / love / want",
          accept: { ignoreCase: true, variants: ["at, well, love, want", "at/well/love/want"] },
          tip: "Four blanks = four concepts! At + well + love + want!",
          successtip: "✓ FOUR PERFECT! 👨‍🍳 Complete integration! Module 3 mastered!"
        },
        {
          type: "mc",
          q: "FINAL READING: Read: 'Maria is really good at English because she studies very carefully every day. She can speak quite well now, so she wants to get a better job. She loves learning and would like to go to college. She thinks education is very important.' — How many Module 3 concepts?",
          options: ["Eight+ (really, good at, because, carefully, can, quite, so, want, love, would like, think)", "Five", "Three"],
          a: "Eight+ (really, good at, because, carefully, can, quite, so, want, love, would like, think)",
          tip: "Count all: modifiers, abilities, connectors, adverbs, desires, opinions!",
          successtip: "✓ EIGHT+! Maximum integration! 📚 Every Unit 11-16! B1 reading! Module 3 MASTERED!"
        },
        {
          type: "mc",
          q: "🏆 CONGRATULATIONS! You completed Module 3 Review! What level are you now?",
          options: ["B1 (Pre-Intermediate) - Ready for real conversations!", "A2 (Elementary)", "A1 (Beginner)"],
          a: "B1 (Pre-Intermediate) - Ready for real conversations!",
          tip: "You integrated abilities, adverbs, modifiers, connectors, desires, and opinions! That's B1!",
          successtip: "✓ A1 LEVEL! 🏆🎉 MÓDULO 3 COMPLETE! You can now: express abilities, describe actions, intensify, connect ideas logically, express desires, give opinions, and make social plans! You're ready for REAL English conversations! 745 questions mastered (Units 11-16 + Reading + Review)! Congratulations! 🚀🌟 Next: Module 4!"
        }
      ]
    }
  ]
};
